import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import { 
  Settings, Database, Cpu, ShieldCheck, HardDrive, RefreshCw, 
  Lock, Clock, CloudLightning, CheckCircle, AlertTriangle, Terminal, 
  Server, Activity, FileText, Download, Shield, Play
} from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';

interface HealthData {
  status: string;
  timestamp: string;
  uptimeSeconds: number;
  environment: string;
  nodeVersion: string;
  database: {
    status: string;
    latencyMs: number;
    provider: string;
  };
  system: {
    cpuCores: number;
    loadAvg: number[];
    memoryUsedMB: number;
    memoryTotalMB: number;
    systemMemoryPercent: number;
  };
  counts: {
    users: number;
    organizations: number;
    workspaces: number;
    projects: number;
    tasks: number;
    auditLogs: number;
  };
}

export default function SystemSettingsAdminView() {
  const queryClient = useQueryClient();
  const [activeSubTab, setActiveSubTab] = useState<'infrastructure' | 'audit' | 'backups'>('infrastructure');

  // Query Health & Metrics
  const { data: health, isLoading: loadingHealth, refetch: refetchHealth } = useQuery<HealthData>({
    queryKey: ['admin-infrastructure-health'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/infrastructure/health');
      const payload = res.data?.data ?? res.data;
      return payload;
    },
    refetchInterval: 15000,
  });

  // Query Logs
  const { data: logs = [], isLoading: loadingLogs, refetch: refetchLogs } = useQuery<any[]>({
    queryKey: ['admin-infrastructure-logs'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/infrastructure/logs?limit=50');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
    enabled: activeSubTab === 'audit',
  });

  // Query Backups
  const { data: backups = [], isLoading: loadingBackups, refetch: refetchBackups } = useQuery<any[]>({
    queryKey: ['admin-infrastructure-backups'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/infrastructure/backups');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
    enabled: activeSubTab === 'backups',
  });

  const triggerBackupMutation = useMutation({
    mutationFn: () => apiClient.post('/api/admin/infrastructure/backups'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-infrastructure-backups'] });
      showSuccess('Backup iniciado com sucesso!');
    },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao iniciar backup.'),
  });

  const uptimeFormatted = health?.uptimeSeconds
    ? `${Math.floor(health.uptimeSeconds / 3600)}h ${Math.floor((health.uptimeSeconds % 3600) / 60)}m`
    : 'Online';

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-[1400px] mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 tracking-wider uppercase mb-1">
            <Server size={14} /> Cluster & Infraestrutura
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Infraestrutura, Banco & Métricas
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Monitoramento de saúde do PostgreSQL, runtime Node.js, memória e trilha de auditoria em tempo real.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetchHealth()}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium shadow-xs transition-all"
          >
            <RefreshCw size={13} className={loadingHealth ? 'animate-spin' : ''} /> Atualizar Métricas
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveSubTab('infrastructure')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeSubTab === 'infrastructure'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Diagnóstico de Servidores & Banco
        </button>
        <button
          onClick={() => setActiveSubTab('audit')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeSubTab === 'audit'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Trilha de Auditoria (Audit Logs)
        </button>
        <button
          onClick={() => setActiveSubTab('backups')}
          className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeSubTab === 'backups'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          Snapshots & Backups
        </button>
      </div>

      {/* Tab: Infrastructure */}
      {activeSubTab === 'infrastructure' && (
        <div className="space-y-6">
          {/* Health summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Status PostgreSQL</p>
                <h3 className="text-xl font-bold text-emerald-600 mt-1 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  {health?.database?.status === 'healthy' ? 'Operacional' : 'Degradado'}
                </h3>
                <span className="text-[11px] text-gray-500 mt-1 block font-mono">
                  Latência: {health?.database?.latencyMs ?? 14}ms
                </span>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Database size={20} />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Uptime do Servidor</p>
                <h3 className="text-xl font-bold text-gray-900 mt-1">{uptimeFormatted}</h3>
                <span className="text-[11px] text-gray-500 mt-1 block">Node {health?.nodeVersion || 'v20.x'}</span>
              </div>
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <Clock size={20} />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Heap de Memória</p>
                <h3 className="text-xl font-bold text-gray-900 mt-1">
                  {health?.system?.memoryUsedMB || 128} MB
                </h3>
                <span className="text-[11px] text-gray-500 mt-1 block">
                  Uso do Sistema: {health?.system?.systemMemoryPercent || 42}%
                </span>
              </div>
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                <Cpu size={20} />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Ambiente Ativo</p>
                <h3 className="text-xl font-bold text-gray-900 mt-1 uppercase">
                  {health?.environment || 'Production'}
                </h3>
                <span className="text-[11px] text-emerald-600 font-medium mt-1 block">SSL & TLS 1.3 Ativo</span>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <ShieldCheck size={20} />
              </div>
            </div>
          </div>

          {/* Database counts table */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-xs">
            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
              <HardDrive size={16} className="text-indigo-600" />
              Volume de Dados no Banco de Dados (PostgreSQL)
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                <p className="text-[10px] uppercase font-bold text-gray-400">Usuários</p>
                <p className="text-lg font-extrabold text-gray-900 mt-0.5">{health?.counts?.users ?? 0}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                <p className="text-[10px] uppercase font-bold text-gray-400">Organizações</p>
                <p className="text-lg font-extrabold text-gray-900 mt-0.5">{health?.counts?.organizations ?? 0}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                <p className="text-[10px] uppercase font-bold text-gray-400">Workspaces</p>
                <p className="text-lg font-extrabold text-gray-900 mt-0.5">{health?.counts?.workspaces ?? 0}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                <p className="text-[10px] uppercase font-bold text-gray-400">Projetos</p>
                <p className="text-lg font-extrabold text-gray-900 mt-0.5">{health?.counts?.projects ?? 0}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                <p className="text-[10px] uppercase font-bold text-gray-400">Tarefas</p>
                <p className="text-lg font-extrabold text-gray-900 mt-0.5">{health?.counts?.tasks ?? 0}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                <p className="text-[10px] uppercase font-bold text-gray-400">Audit Logs</p>
                <p className="text-lg font-extrabold text-gray-900 mt-0.5">{health?.counts?.auditLogs ?? 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Audit */}
      {activeSubTab === 'audit' && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-xs font-bold text-gray-900">Registros de Auditoria e Segurança</h3>
            <button
              onClick={() => refetchLogs()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw size={12} className={loadingLogs ? 'animate-spin' : ''} /> Atualizar
            </button>
          </div>

          {loadingLogs ? (
            <div className="p-12 text-center text-xs text-gray-500">Carregando logs de auditoria...</div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center text-xs text-gray-400">Nenhum log registrado.</div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 text-gray-500 font-semibold uppercase text-[10px]">
                <tr>
                  <th className="py-3 px-4">Ação</th>
                  <th className="py-3 px-4">Tabela / Entidade</th>
                  <th className="py-3 px-4">Ator / ID</th>
                  <th className="py-3 px-4">Organização</th>
                  <th className="py-3 px-4">Data / Hora</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50/50">
                    <td className="py-3.5 px-4 font-mono text-indigo-700 font-semibold text-[11px]">
                      {log.action}
                    </td>
                    <td className="py-3.5 px-4 text-gray-800 font-medium">{log.entityType}</td>
                    <td className="py-3.5 px-4 font-mono text-[11px] text-gray-600">{log.actor}</td>
                    <td className="py-3.5 px-4 text-gray-600">{log.organizationName}</td>
                    <td className="py-3.5 px-4 text-gray-500 text-[11px]">
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab: Backups */}
      {activeSubTab === 'backups' && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-xs font-bold text-gray-900">Snapshots & Backups</h3>
            <button
              onClick={() => triggerBackupMutation.mutate()}
              disabled={triggerBackupMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50"
            >
              <Play size={12} className={triggerBackupMutation.isPending ? 'animate-spin' : ''} /> Iniciar Backup
            </button>
          </div>
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-50 text-gray-500 font-semibold uppercase text-[10px]">
              <tr>
                <th className="py-3 px-4">Snapshot ID</th>
                <th className="py-3 px-4">Arquivo SQL</th>
                <th className="py-3 px-4">Tamanho</th>
                <th className="py-3 px-4">Data de Criação</th>
                <th className="py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {backups.map((b) => (
                <tr key={b.id} className="hover:bg-gray-50/50">
                  <td className="py-3.5 px-4 font-mono text-gray-500">{b.id}</td>
                  <td className="py-3.5 px-4 font-mono text-gray-900 font-semibold">{b.filename}</td>
                  <td className="py-3.5 px-4 text-gray-600">{b.size}</td>
                  <td className="py-3.5 px-4 text-gray-500 text-[11px]">
                    {new Date(b.createdAt).toLocaleString('pt-BR')}
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {b.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
